//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  BVSamplingProduct,
  BVSamplingProductVariationAllocation,
  SamplingSocialTask,
} from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';

export interface ProductQuantityRow {
  id: string;
  instagram: string;
  isSingleVariation: boolean;
  name: string | undefined;
  productId: string | undefined;
  productIndex: number;
  quantity: number | undefined;
  tiktok: string;
  variantName: string | undefined;
  variationIndex: number | undefined;
}

export type AllocationInputKey = `${string}-${SamplingSocialTask}`;

export type AllocationError = 'invalidFormat' | 'overLimit';

export const getAllocationInputKey = (
  product: ProductQuantityRow,
  platform: SamplingSocialTask,
): AllocationInputKey => `${product.id}-${platform}`;

export const getProductQuantityRowId = (
  productIndex: number,
  variationIndex: number,
): string => `product-${productIndex}-variation-${variationIndex}`;

export const getAllocationQuantity = (
  allocations: { quantity: number; tasks: SamplingSocialTask[] }[] | undefined,
  task: SamplingSocialTask,
): number | undefined =>
  allocations?.find(({ tasks }) => tasks.includes(task))?.quantity;

export const getProductVariationQuantity = (
  product: BVSamplingProduct,
  variation: { quantity?: number },
): number | undefined =>
  product.variations?.length === 1
    ? product.quantity ?? variation.quantity
    : variation.quantity;

export const getAllocationError = (
  value: string,
  totalQuantity: number | undefined,
  emptyIsInvalid = false,
): AllocationError | undefined => {
  if (!value) {
    return emptyIsInvalid ? 'invalidFormat' : undefined;
  }

  const quantity = Number(value);
  if (
    !/^\d+$/.test(value) ||
    !Number.isFinite(quantity) ||
    !Number.isInteger(quantity)
  ) {
    return 'invalidFormat';
  }

  const maximum = totalQuantity ?? 0;
  if (quantity > maximum) {
    return 'overLimit';
  }

  return undefined;
};

export const hasInvalidAllocationInputs = (
  products: ProductQuantityRow[],
  allocationInputValues: Partial<Record<AllocationInputKey, string>>,
  emptyIsInvalid = false,
): boolean =>
  products.some((product) =>
    (['instagram', 'tiktok'] as const).some((platform) =>
      Boolean(
        getAllocationError(
          allocationInputValues[getAllocationInputKey(product, platform)] ??
            (platform === 'instagram' ? product.instagram : product.tiktok),
          product.quantity,
          emptyIsInvalid,
        ),
      ),
    ),
  );

export const clearInvalidAllocations = (
  products: BVSamplingProduct[],
): BVSamplingProduct[] =>
  products.map((product) => ({
    ...product,
    variations: product.variations?.map((variation) =>
      hasCompleteAllocation(
        variation,
        getProductVariationQuantity(product, variation),
      ) || isCustomAllocation(variation)
        ? variation
        : { ...variation, allocations: undefined },
    ),
  }));

export const isCustomAllocation = (variation: {
  allocations?: { quantity: number; tasks: SamplingSocialTask[] }[];
}): boolean => {
  const instagramQuantity = getAllocationQuantity(
    variation.allocations,
    'instagram',
  );
  const tiktokQuantity = getAllocationQuantity(variation.allocations, 'tiktok');

  if (
    !isValidAllocationQuantity(instagramQuantity) ||
    !isValidAllocationQuantity(tiktokQuantity)
  ) {
    return false;
  }

  if (instagramQuantity === 0 && tiktokQuantity === 0) {
    return true;
  }

  const allocationQuantity = instagramQuantity + tiktokQuantity;
  return (
    instagramQuantity !== Math.ceil(allocationQuantity / 2) ||
    tiktokQuantity !== Math.floor(allocationQuantity / 2)
  );
};

const hasCompleteAllocation = (
  variation: {
    allocations?: { quantity: number; tasks: SamplingSocialTask[] }[];
  },
  totalQuantity: number | undefined,
): boolean => {
  const instagramQuantity = getAllocationQuantity(
    variation.allocations,
    'instagram',
  );
  const tiktokQuantity = getAllocationQuantity(variation.allocations, 'tiktok');

  return (
    isValidAllocationQuantity(instagramQuantity, totalQuantity) &&
    isValidAllocationQuantity(tiktokQuantity, totalQuantity) &&
    instagramQuantity + tiktokQuantity === totalQuantity
  );
};

export const calculateAllocations = (
  products: BVSamplingProduct[],
): BVSamplingProduct[] => {
  let hasNewAllocations = false;
  const allocatedProducts = products.map((product) => {
    const variations = product.variations?.map((variation) => {
      const quantity = getProductVariationQuantity(product, variation) ?? 0;

      if (
        hasCompleteAllocation(variation, quantity) ||
        isCustomAllocation(variation)
      ) {
        return variation;
      }

      hasNewAllocations = true;
      const instagramQuantity = Math.ceil(quantity / 2);

      return {
        ...variation,
        allocations: [
          { quantity: instagramQuantity, tasks: ['instagram'] },
          { quantity: quantity - instagramQuantity, tasks: ['tiktok'] },
        ] satisfies BVSamplingProductVariationAllocation[],
      };
    });

    return variations === product.variations
      ? product
      : { ...product, variations };
  });

  return hasNewAllocations ? allocatedProducts : products;
};

const isValidAllocationQuantity = (
  quantity: number | undefined,
  totalQuantity?: number,
): quantity is number =>
  typeof quantity === 'number' &&
  Number.isInteger(quantity) &&
  quantity >= 0 &&
  quantity <= (totalQuantity ?? Infinity);
